/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  BulkCollectionsRequest,
  BulkResponse,
  Collection,
  DeleteAdminV1StoresStoreIdCollectionsIdPathParameters,
  DeleteAdminV1StoresStoreIdCollectionsIdProductsProductIdPathParameters,
  GetAdminV1StoresStoreIdCollections200,
  GetAdminV1StoresStoreIdCollectionsIdPathParameters,
  GetAdminV1StoresStoreIdCollectionsIdProducts200,
  GetAdminV1StoresStoreIdCollectionsIdProductsPathParameters,
  GetAdminV1StoresStoreIdCollectionsParams,
  GetAdminV1StoresStoreIdCollectionsPathParameters,
  GetAdminV1StoresStoreIdProductsProductIdCollectionsPathParameters,
  PatchAdminV1StoresStoreIdCollectionsIdBody,
  PatchAdminV1StoresStoreIdCollectionsIdPathParameters,
  PostAdminV1StoresStoreIdCollectionsBody,
  PostAdminV1StoresStoreIdCollectionsBulkPathParameters,
  PostAdminV1StoresStoreIdCollectionsIdProducts200,
  PostAdminV1StoresStoreIdCollectionsIdProductsBody,
  PostAdminV1StoresStoreIdCollectionsIdProductsPathParameters,
  PostAdminV1StoresStoreIdCollectionsIdRefreshPathParameters,
  PostAdminV1StoresStoreIdCollectionsPathParameters,
  PostAdminV1StoresStoreIdCollectionsPreviewRulesBody,
  PostAdminV1StoresStoreIdCollectionsPreviewRulesPathParameters,
  PreviewRulesResponse,
  ProductCollectionsResponse,
  PutAdminV1StoresStoreIdCollectionsIdProducts200,
  PutAdminV1StoresStoreIdCollectionsIdProductsBody,
  PutAdminV1StoresStoreIdCollectionsIdProductsPathParameters,
  PutAdminV1StoresStoreIdProductsProductIdCollectionsBody,
  PutAdminV1StoresStoreIdProductsProductIdCollectionsPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Collections of the store
 */
export const getAdminV1StoresStoreIdCollections = (
    { storeId }: GetAdminV1StoresStoreIdCollectionsPathParameters,
    params?: GetAdminV1StoresStoreIdCollectionsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdCollections200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdCollections200>(
      {url: `/admin/v1/stores/${storeId}/collections`, method: 'GET',
        params
    },
      options);
    }
  /**
 * An automated collection needs rules and is materialized right away and again by the worker sweep; a manual one takes its products through PUT .../products
 * @summary Create a manual or automated collection
 */
export const postAdminV1StoresStoreIdCollections = (
    { storeId }: PostAdminV1StoresStoreIdCollectionsPathParameters,
    postAdminV1StoresStoreIdCollectionsBody: BodyType<PostAdminV1StoresStoreIdCollectionsBody>,
 options?: SecondParameter<typeof customInstance<Collection>>,) => {
      return customInstance<Collection>(
      {url: `/admin/v1/stores/${storeId}/collections`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCollectionsBody
    },
      options);
    }
  /**
 * @summary Get a collection
 */
export const getAdminV1StoresStoreIdCollectionsId = (
    { storeId, id }: GetAdminV1StoresStoreIdCollectionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<Collection>>,) => {
      return customInstance<Collection>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a collection; new rules re-materialize an automated one
 */
export const patchAdminV1StoresStoreIdCollectionsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdCollectionsIdPathParameters,
    patchAdminV1StoresStoreIdCollectionsIdBody: BodyType<PatchAdminV1StoresStoreIdCollectionsIdBody>,
 options?: SecondParameter<typeof customInstance<Collection>>,) => {
      return customInstance<Collection>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdCollectionsIdBody
    },
      options);
    }
  /**
 * @summary Delete a collection
 */
export const deleteAdminV1StoresStoreIdCollectionsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdCollectionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Products in the collection, in position order
 */
export const getAdminV1StoresStoreIdCollectionsIdProducts = (
    { storeId, id }: GetAdminV1StoresStoreIdCollectionsIdProductsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdCollectionsIdProducts200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdCollectionsIdProducts200>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}/products`, method: 'GET'
    },
      options);
    }
  /**
 * Order is the display order, at most 2,000 products. Every product must already be in the store; 409 on automated collections. POST .../products appends and DELETE .../products/{productId} removes one without resending the list
 * @summary Replace the product list of a manual collection
 */
export const putAdminV1StoresStoreIdCollectionsIdProducts = (
    { storeId, id }: PutAdminV1StoresStoreIdCollectionsIdProductsPathParameters,
    putAdminV1StoresStoreIdCollectionsIdProductsBody: BodyType<PutAdminV1StoresStoreIdCollectionsIdProductsBody>,
 options?: SecondParameter<typeof customInstance<PutAdminV1StoresStoreIdCollectionsIdProducts200>>,) => {
      return customInstance<PutAdminV1StoresStoreIdCollectionsIdProducts200>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}/products`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdCollectionsIdProductsBody
    },
      options);
    }
  /**
 * Idempotent: ids already in the collection keep their position. Takes productIds or a store-scope selection (422 INVALID_INPUT for both or neither); a selection resolves inside the call, 409 SELECTION_CHANGED when its filter count moved, 422 SELECTION_TOO_LARGE past 5,000. 422 LIMIT_EXCEEDED past 2,000 members, 422 INVALID_REFERENCE for ids not in the store, 409 on automated collections
 * @summary Add products to the end of a manual collection
 */
export const postAdminV1StoresStoreIdCollectionsIdProducts = (
    { storeId, id }: PostAdminV1StoresStoreIdCollectionsIdProductsPathParameters,
    postAdminV1StoresStoreIdCollectionsIdProductsBody: BodyType<PostAdminV1StoresStoreIdCollectionsIdProductsBody>,
 options?: SecondParameter<typeof customInstance<PostAdminV1StoresStoreIdCollectionsIdProducts200>>,) => {
      return customInstance<PostAdminV1StoresStoreIdCollectionsIdProducts200>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}/products`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCollectionsIdProductsBody
    },
      options);
    }
  /**
 * @summary Remove one product from a manual collection
 */
export const deleteAdminV1StoresStoreIdCollectionsIdProductsProductId = (
    { storeId, id, productId }: DeleteAdminV1StoresStoreIdCollectionsIdProductsProductIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}/products/${productId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * set_status or delete. Each id is answered with the status a single request would have had; 200 when all succeeded, 207 otherwise
 * @summary Apply one action to many collections
 */
export const postAdminV1StoresStoreIdCollectionsBulk = (
    { storeId }: PostAdminV1StoresStoreIdCollectionsBulkPathParameters,
    bulkCollectionsRequest: BodyType<BulkCollectionsRequest>,
 options?: SecondParameter<typeof customInstance<BulkResponse>>,) => {
      return customInstance<BulkResponse>(
      {url: `/admin/v1/stores/${storeId}/collections/bulk`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: bulkCollectionsRequest
    },
      options);
    }
  /**
 * `published: false` when the product is not in this store; 404 when it is not in the organization
 * @summary Every collection of the store with the product's membership
 */
export const getAdminV1StoresStoreIdProductsProductIdCollections = (
    { storeId, productId }: GetAdminV1StoresStoreIdProductsProductIdCollectionsPathParameters,
 options?: SecondParameter<typeof customInstance<ProductCollectionsResponse>>,) => {
      return customInstance<ProductCollectionsResponse>(
      {url: `/admin/v1/stores/${storeId}/products/${productId}/collections`, method: 'GET'
    },
      options);
    }
  /**
 * Joins listed collections at the end and leaves unlisted manual ones; other members keep their positions. Automated collections are refused (422), as is an unknown id (422 INVALID_REFERENCE); 409 when the product is not published to the store
 * @summary Set which manual collections the product is in
 */
export const putAdminV1StoresStoreIdProductsProductIdCollections = (
    { storeId, productId }: PutAdminV1StoresStoreIdProductsProductIdCollectionsPathParameters,
    putAdminV1StoresStoreIdProductsProductIdCollectionsBody: BodyType<PutAdminV1StoresStoreIdProductsProductIdCollectionsBody>,
 options?: SecondParameter<typeof customInstance<ProductCollectionsResponse>>,) => {
      return customInstance<ProductCollectionsResponse>(
      {url: `/admin/v1/stores/${storeId}/products/${productId}/collections`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdProductsProductIdCollectionsBody
    },
      options);
    }
  /**
 * @summary Evaluate rules against the store's published products without saving
 */
export const postAdminV1StoresStoreIdCollectionsPreviewRules = (
    { storeId }: PostAdminV1StoresStoreIdCollectionsPreviewRulesPathParameters,
    postAdminV1StoresStoreIdCollectionsPreviewRulesBody: BodyType<PostAdminV1StoresStoreIdCollectionsPreviewRulesBody>,
 options?: SecondParameter<typeof customInstance<PreviewRulesResponse>>,) => {
      return customInstance<PreviewRulesResponse>(
      {url: `/admin/v1/stores/${storeId}/collections/preview-rules`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCollectionsPreviewRulesBody
    },
      options);
    }
  /**
 * @summary Re-materialize an automated collection now
 */
export const postAdminV1StoresStoreIdCollectionsIdRefresh = (
    { storeId, id }: PostAdminV1StoresStoreIdCollectionsIdRefreshPathParameters,
 options?: SecondParameter<typeof customInstance<Collection>>,) => {
      return customInstance<Collection>(
      {url: `/admin/v1/stores/${storeId}/collections/${id}/refresh`, method: 'POST'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdCollectionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCollections>>>
export type PostAdminV1StoresStoreIdCollectionsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCollections>>>
export type GetAdminV1StoresStoreIdCollectionsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCollectionsId>>>
export type PatchAdminV1StoresStoreIdCollectionsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdCollectionsId>>>
export type DeleteAdminV1StoresStoreIdCollectionsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdCollectionsId>>>
export type GetAdminV1StoresStoreIdCollectionsIdProductsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCollectionsIdProducts>>>
export type PutAdminV1StoresStoreIdCollectionsIdProductsResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdCollectionsIdProducts>>>
export type PostAdminV1StoresStoreIdCollectionsIdProductsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCollectionsIdProducts>>>
export type DeleteAdminV1StoresStoreIdCollectionsIdProductsProductIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdCollectionsIdProductsProductId>>>
export type PostAdminV1StoresStoreIdCollectionsBulkResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCollectionsBulk>>>
export type GetAdminV1StoresStoreIdProductsProductIdCollectionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdProductsProductIdCollections>>>
export type PutAdminV1StoresStoreIdProductsProductIdCollectionsResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdProductsProductIdCollections>>>
export type PostAdminV1StoresStoreIdCollectionsPreviewRulesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCollectionsPreviewRules>>>
export type PostAdminV1StoresStoreIdCollectionsIdRefreshResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCollectionsIdRefresh>>>
